class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.renderDeliveryEstimate();
    // Quantity updates replace .drawer__inner via CartItems without calling
    // renderContents, so re-populate the estimate on every cart update too.
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.renderDeliveryEstimate());
  }

  renderDeliveryEstimate() {
    const estimate = this.querySelector('#CartDrawerDeliveryEstimate');
    if (!estimate) return;

    let postcode;
    try {
      // localStorage access can throw in private browsing modes
      postcode = window.localStorage.getItem('kut_delivery_postcode');
    } catch (error) {
      return;
    }
    if (!postcode) return;
    postcode = postcode.trim();

    if (!CartDrawer.deliveryZonesPromise) {
      CartDrawer.deliveryZonesPromise = fetch(estimate.dataset.zonesUrl)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
        .then((zonesData) => {
          // Cache only success — a transient failure must not suppress the
          // estimate for the rest of the session.
          if (!zonesData) CartDrawer.deliveryZonesPromise = null;
          return zonesData;
        });
    }

    CartDrawer.deliveryZonesPromise.then((zonesData) => {
      const zone = zonesData && zonesData.zones && zonesData.zones[postcode];
      const price = zone && Number(zone.price);
      if (!zone || !Number.isFinite(price)) return;
      // Re-query: a renderContents may have replaced the node while fetching
      const target = this.querySelector('#CartDrawerDeliveryEstimate');
      if (!target) return;
      const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price);
      // Zones 3-6 are bare digits in the zones file; SA is "Metro (Delivered From Melbourne)".
      const raw = String(zone.zone || '');
      const label = raw.indexOf('Metro') === 0 ? (raw.indexOf('Melbourne') !== -1 ? 'Metro, delivered from Melbourne' : 'Metro') : `Zone ${raw}`;
      target.textContent = `Delivery estimate to ${postcode}: ${money} (${label}) — confirmed with your order.`;
      target.removeAttribute('hidden');
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.renderDeliveryEstimate();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
